import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Sidebar } from "./components/Sidebar";
import { Dashboard } from "./components/Dashboard";
import { QRCodes } from "./components/QRCodes";
import { Feedbacks } from "./components/Feedbacks";
import { Users } from "./components/Users";
import { Orders } from "./components/Orders";
import { ScanLogs } from "./components/ScanLogs";
import { Settings } from "./components/Settings";
import { Packages } from "./components/Packages";
import { Toaster } from "./components/ui/sonner";
import { Login } from "./components/Login";
import { ForgotPassword } from "./components/ForgotPassword";
import { ResetPassword } from "./components/ResetPassword";
import { Newsletters } from "./components/Newsletters";
import { Subscribers } from "./components/Subscribers";
import { SupportTickets } from "./components/SupportTickets";
import { Coupons } from "./components/Coupons";
import { Visitors } from "./components/Visitors";
import { useAuth } from "./hooks/useAuth";
import { useState } from "react";

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Layout Component for authenticated pages
function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  
  // Extract the current route from the pathname
  const currentRoute = location.pathname.slice(1) || 'dashboard';

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <Sidebar
        activeScreen={currentRoute}
        onScreenChange={(screen) => {
          // This will be handled by navigation links in the sidebar
        }}
        isCollapsed={sidebarCollapsed}
        onToggleCollapsed={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Main Content Area */}
        <main className="flex-1 overflow-auto p-8">
          {children}
        </main>
      </div>
    </div>
  );
}

export default function App() {
  const { user, isLoading } = useAuth();
  console.log("version: 1.0.2");
  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <>
        <div className="flex h-screen items-center justify-center bg-background">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
        <Toaster />
      </>
    );
  }

  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/login" 
          element={
            user ? <Navigate to="/dashboard" replace /> : <Login />
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />
          } 
        />
        <Route 
          path="/reset-password" 
          element={
            user ? <Navigate to="/dashboard" replace /> : <ResetPassword />
          } 
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Dashboard />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/qrcodes"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <QRCodes />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Users />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/visitors"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Visitors />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Orders />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/newsletters"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Newsletters />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscribers"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Subscribers />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/support-tickets"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <SupportTickets />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/coupons"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Coupons />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/feedbacks"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Feedbacks />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/scanlogs"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <ScanLogs />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/packages"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Packages />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AuthenticatedLayout>
                <Settings />
              </AuthenticatedLayout>
            </ProtectedRoute>
          }
        />

        {/* Default redirects */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      {/* Toast Notifications */}
      <Toaster />
    </>
  );
}
